import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export const DESKTOP_SIDEBAR_COLLAPSED_KEY = 'desktopSidebarCollapsed';

interface LayoutContextValue {
  desktopSidebarCollapsed: boolean;
  setDesktopSidebarCollapsed: (collapsed: boolean) => void;
}

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(DESKTOP_SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DESKTOP_SIDEBAR_COLLAPSED_KEY, desktopSidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [desktopSidebarCollapsed]);

  return (
    <LayoutContext.Provider value={{ desktopSidebarCollapsed, setDesktopSidebarCollapsed }}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayoutSidebar = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayoutSidebar must be used within a LayoutProvider');
  }
  return context;
};

/** Safe for components that may read sidebar width; defaults to expanded when outside Layout. */
export const useDesktopSidebarCollapsed = (): boolean => {
  return useContext(LayoutContext)?.desktopSidebarCollapsed ?? false;
};
